"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createMetricSubclass = void 0;
const createEndEvent_1 = __importDefault(require("../events/createEndEvent"));
/**
 * Base class for Web Vitals metrics
 */
class WebVitalsMetric {
    /**
     * Create a WebVitalsMetric instance.
     *
     * @param {string} metricName - Name of the Web Vitals metric
     * @param {Function} reporter - Web Vitals metric reporter function
     * @param {Debug} Debug - Global Debug instance
     * @param {*} opts - Configuration options. All options except
     *   `defaultValue` are required.
     *   if the metric's score rating is worse than "good".)
     * @param {number|undefined} [opts.defaultValue] - Optional default value to
     *   return in an end event object if the metric was never reported
     * @param {number|undefined} [opts.goodThreshold] - Highest value a score
     *   can be to receive a "good" rating. (Attribution will only be reported
     * @param {Function|undefined} [opts.processReport] - Optional callback to
     *   preprocess the metric report before it gets stored. The callback will
     *   receive the raw metric report and the global debug instance.
     */
    constructor(metricName, reporter, debug, { defaultValue, goodThreshold, processReport } = {}) {
        this.metricName = metricName;
        this.reporter = reporter;
        this.debug = debug;
        // Optional
        this.defaultValue = defaultValue;
        this.goodThreshold = goodThreshold;
        this.processReport = processReport;
        // Store the latest report of the given metric
        this.latestReport = { attribution: null, value: null };
        // Set up Web Vitals metric reporting. We `reportAllChanges` but only
        // keep the most recent value to protect against final reports firing
        // _after_ our final beacon on page unload. (Especially important with
        // CLS and INP which always fire their final reports on page unload.)
        this.reporter(this.createReporter(), { reportAllChanges: true });
    }
    /**
     * Create a report callback function which will store the latest report of
     * the given metric when called by its metric reporter. If the
     * `processReport` callback is provided, it will be called with the raw
     * metric report and the global debug instance before the report is stored.
     * @private
     */
    createReporter() {
        return metricReport => {
            let newMetricReport = metricReport;
            this.debug.log(`${this.metricName} reported`, metricReport);
            // Preprocess report if a processReport callback was provided
            if (this.processReport) {
                newMetricReport = this.processReport(metricReport, this.debug);
                this.debug.log(`${this.metricName} report processed`, newMetricReport);
            }
            // Merge latest report with existing report
            this.latestReport = Object.assign(Object.assign({}, this.latestReport), newMetricReport);
        };
    }
    /**
     * Return the latest metric value (rounded to the nearest integer) as an end
     * event object. If the metric was never reported, `null` will be returned
     * instead of an event object, unless a `defaultValue` was provided, in
     * which case an end event object with the `defaultValue` will be returned.
     *
     * An optional `metricNameOverride` may be provided to override the metric
     * name in the end event object.
     *
     * @param {string|undefined} [metricNameOverride] - Optional metric name to
     *   use in the end event object instead of the default metric name
     * @returns {EndEvent|null} - Latest metric value as an end event object, or
     *   `null` if the metric was never reported. If a `defaultValue` was set an
     *   end event object with the `defaultValue` will be returned instead.
     */
    getValueEvent(metricNameOverride) {
        const metricValue = this.latestReport.value;
        const metricName = metricNameOverride || this.metricName;
        if (metricValue === null) {
            if (this.defaultValue === undefined)
                return null;
            return (0, createEndEvent_1.default)(metricName, this.defaultValue);
        }
        return (0, createEndEvent_1.default)(metricName, metricValue);
    }
    /**
     * Get the latest attribution details from if the metric score rating is
     * worse than "good". If the metric score rating is "good" or better, `null`
     * will be returned (b/c we assume we don't need the extra attribution
     * details for metrics that are already considered "good".)
     *
     * If `this.goodThreshold` is not provided _or_ debug mode is enabled, this
     * check will be skipped entirely and the attribution details will always be
     * returned. (Or `null` if the metric was never reported.)
     *
     * @see [web-vitals.js attribution interface
     * docs](https://github.com/GoogleChrome/web-vitals#attribution)
     */
    getAttribution() {
        if (this.debug.isEnabled() ||
            !this.goodThreshold ||
            this.latestReport.value > this.goodThreshold) {
            return this.latestReport.attribution;
        }
        return null;
    }
}
/**
 * Creates a subclass of WebVitalsMetric with the given metric name, reporter,
 * and debug instance.
 *
 * @param {string} metricName - Name of the Web Vitals metric
 * @param {Function} reporter - Web Vitals metric reporter function
 * @param {Debug} debug - Global Debug instance
 * @param {number|undefined} [goodThreshold] - Highest value a score can be to
 *   receive a "good" rating. (Attribution will only be reported if the metric's
 *   score rating is worse than "good".)
 *
 * @returns {typeof WebVitalsMetric} A subclass of WebVitalsMetric
 */
const createMetricSubclass = (metricName, reporter, debug, goodThreshold) => class extends WebVitalsMetric {
    constructor() {
        super(metricName, reporter, debug, { goodThreshold });
    }
};
exports.createMetricSubclass = createMetricSubclass;
// Exports
exports.default = WebVitalsMetric;
